import { Component, input, output, model, OnDestroy, ElementRef, viewChild, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { VoiceAttachment } from '@/app/services/mock-report.service';

/** 确认卡片字段定义（用于渲染可编辑表单） */
export interface ConfirmFieldDef {
  key: string;
  label: string;
  /** 是否可编辑（预填的车辆基础字段可只读展示，语音字段可编辑） */
  editable?: boolean;
  /** 是否多行文本 */
  textarea?: boolean;
  /** 是否为布尔字段（如"是否带挂"），渲染为是/否选项而非文本输入 */
  boolean?: boolean;
}

/**
 * 报告确认卡片组件。
 *
 * 语音报报告流程结束后弹出，展示 chatflow 采集到的结构化字段，
 * 用户可编辑语音采集字段后确认。确认后通过 onConfirm 回调提交报告。
 */
@Component({
  selector: 'app-report-confirm-card',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule],
  templateUrl: './report-confirm-card.component.html',
  styleUrl: './report-confirm-card.component.scss',
})
export class ReportConfirmCardComponent implements OnDestroy {
  /** 报告类型 */
  reportType = input.required<'OTI' | 'LP'>();
  /** 字段定义列表（控制展示顺序与标签） */
  fieldDefs = input<ConfirmFieldDef[]>([]);
  /** 结构化字段值（可编辑） */
  fields = model<Record<string, unknown>>({});
  /** 由系统自动补全日期的时间字段（需提醒用户确认日期是否正确） */
  timeAutoComplected = input<string[]>([]);
  /** 是否已提交：已提交后不再展示图片上传与确认提交模块，仅只读展示 */
  submitted = input(false);

  private readonly fileInputRef = viewChild<ElementRef<HTMLInputElement>>('fileInput');
  private readonly translate = inject(TranslateService);

  /** 已选择的附件（POC 本地预览，不真实上传 S3） */
  attachments: VoiceAttachment[] = [];

  /** 字段标签翻译（回退到传入的 label） */
  protected fieldLabel(key: string, fallback: string): string {
    return this.translate.instant(`voiceReport.field.${key}`) || fallback;
  }

  /** 某字段的日期是否为自动补全（需要用户确认） */
  protected isTimeAutoComplected(key: string): boolean {
    return this.timeAutoComplected().includes(key);
  }

  /** 确认提交事件（携带结构化字段 + 附件） */
  confirm = output<Record<string, unknown>>();
  /** 取消事件 */
  cancel = output<void>();

  /** 选择附件（图片）：本地预览，不真实上传 */
  protected onFilesSelected(event: Event): void {
    const files = (event.target as HTMLInputElement).files;
    if (!files?.length) return;
    for (const file of Array.from(files)) {
      if (!file.type.startsWith('image/')) continue;
      this.attachments.push({
        url: URL.createObjectURL(file),
        name: file.name,
        isImage: true,
      });
    }
    // 清空 input，允许重复选择同一文件
    if (this.fileInputRef()?.nativeElement) {
      this.fileInputRef()!.nativeElement.value = '';
    }
  }

  /** 移除某个附件并释放 objectURL */
  protected removeAttachment(index: number): void {
    const [removed] = this.attachments.splice(index, 1);
    if (removed) URL.revokeObjectURL(removed.url);
  }

  protected submit(): void {
    // 把附件附加到输出，调用方保存到 mock 报告
    this.confirm.emit({
      ...this.fields(),
      attachments: this.attachments.map(a => ({ ...a })),
    });
  }

  protected hasValue(key: string): boolean {
    const v = this.fields()[key];
    return v !== undefined && v !== null && v !== '';
  }

  protected setValue(key: string, value: unknown): void {
    this.fields.update(f => ({ ...f, [key]: value }));
  }

  ngOnDestroy(): void {
    // 释放所有本地预览 objectURL，避免内存泄漏
    this.attachments.forEach(a => URL.revokeObjectURL(a.url));
  }
}
